import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ArrowUpRight, Heart } from 'lucide-react';

export const HealingWithinReachPage: React.FC = () => {
    const mainRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const donationLink = "https://www.zeffy.com/en-US/donation-form/healing-within-reach-fund";

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (videoRef.current && prefersReducedMotion) {
            videoRef.current.pause();
        }

        if (prefersReducedMotion) return;

        const ctx = gsap.context(() => {
            // Hero animations
            const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            heroTl.from(logoRef.current, {
                opacity: 0,
                scale: 0.9,
                y: 30,
                duration: 1.2,
            })
                .from('.hero-tagline', {
                    opacity: 0,
                    x: 50,
                    duration: 1,
                }, '-=0.6');

            // Video section animations
            gsap.from('.video-title', {
                opacity: 0,
                scale: 0.9,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.video-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            gsap.from('.video-description', {
                opacity: 0,
                y: 30,
                duration: 0.8,
                delay: 0.2,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.video-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            // Content section fade in
            gsap.from('.content-section', {
                opacity: 0,
                y: 60,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.content-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none'
                }
            });

        }, mainRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={mainRef} className="healing-page bg-white">
            {/* Hero Section - Logo on left, CTA on right */}
            <section className="relative min-h-[45vh] flex items-center bg-white pt-20">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        {/* Left Side - Large Logo */}
                        <div ref={logoRef}>
                            <img
                                src="/images/Healing-Within-Reach-logo-Horizontal.png"
                                alt="Healing Within Reach Fund logo"
                                className="w-[70vw] sm:w-80vw max-w-xs sm:max-w-lg h-auto object-contain"
                            />
                        </div>

                        {/* Right Side - Tagline and CTA */}
                        <div className="flex flex-col justify-center lg:pl-8">
                            <p
                                className="hero-tagline text-lg md:text-xl text-neutral-700 leading-relaxed max-w-md"
                                style={{ fontWeight: 400 }}
                            >
                                A community-driven nonprofit fund dedicated to making mental health support accessible and compassionate for all.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Section - Similar to HomePage Depression Section */}
            <section className="video-section relative h-screen min-h-[600px] flex items-center overflow-hidden">
                <video
                    ref={videoRef}
                    className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/Depressed.jpg"
                >
                    <source src="/videos/Treeofhope.mp4" type="video/mp4" />
                    {/* Fallback image if video doesn't load */}
                    <img
                        src="/images/Sunrise.jpg"
                        alt="Healing for all"
                        className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    />
                </video>
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl">
                        <h2 className="video-title text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                            Healing Is <span className='text-yellow-500'>Within Reach</span> for All
                        </h2>
                        <p className="video-description text-white leading-relaxed">
                            Our outreach programs connect hundreds with vital mental health resources,<br />
                            fostering emotional strength, clarity, and hope across the communities we serve.
                        </p>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="content-section py-24 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Decorative accent line */}
                    <div className="w-20 h-1 bg-[#d68800] mb-8 rounded-full"></div>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-xl md:text-2xl text-mscg-dark-gray leading-relaxed mb-8 font-medium">
                            This year, our team of trauma-informed therapists and advocates worked alongside local organizations to provide free counseling sessions, host wellness workshops, and expand telehealth services across underserved communities.
                        </p>

                        <p className="text-lg text-mscg-dark-gray leading-relaxed mb-6">
                            We've empowered individuals and families to begin their healing journeys in safe, welcoming environments. Our outreach programs have connected hundreds with vital resources, fostering resilience and emotional well-being.
                        </p>

                        <p className="text-lg text-mscg-dark-gray leading-relaxed mb-12">
                            With your support, we can continue to grow, ensuring that healing is truly within reach for everyone.
                        </p>

                        {/* CTA - Left aligned with improved styling */}
                        <div
                            className="p-8 md:p-10 rounded-2xl text-left relative overflow-hidden"
                            style={{ backgroundColor: '#293039' }}
                        >
                            {/* Decorative accent */}
                            <div className="absolute top-0 left-0 w-2 h-full bg-[#d68800]"></div>

                            <p className="text-xl md:text-2xl text-white font-bold pl-4">
                                Join us in making mental health care accessible to all.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sticky Donate Button */}
            <a
                href={donationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-8 right-8 z-50 inline-flex items-center gap-3 bg-[#d68800] text-white pl-6 pr-2 py-2 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl group animate-bounce"
            >
                <Heart className="w-5 h-5 text-white" />
                Donate Now
                <div className="w-10 h-10 rounded-full bg-white text-[#d68800] flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:text-white group-hover:rotate-45">
                    <ArrowUpRight className="w-5 h-5" />
                </div>
            </a>
        </div>
    );
};
